import { useEffect, useRef, useState } from 'react';
import { useIsMobile, useToast } from '@hooks';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import * as ApiModels from '@typings/api-models';
import Modal from '@components/Modal';
import ProgressIndicator from '@components/ProgressIndicator';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import XCircle from '@components/Icons/XCircle';
import { SIZES } from '@/styles/media';
import BannerStatus from '@components/DesignLibrary/BannerStatus';
import Spacer from '@components/Spacer';

import Confirm from './Confirm';
import CreditReportForm from './CreditReportForm';
import RequestIdentityVerification from './RequestIdentityVerification';
import ConfirmIdentityVerification from './ConfirmIdentityVerification';
import s from './CreditReportModal.module.scss';

const steps = [
  { content: '', title: 'Enrollment' },
  { content: '', title: 'Identity Verification' },
  { content: '', title: 'Identity Verification' },
  { content: '', title: '' },
];

type Props = {
  creditReportId: string;
  onClose: () => void;
  onConfirm: () => unknown;
};

const CustomCreditReportApprovalModal: React.FC<Props> = ({ creditReportId, onConfirm, onClose }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [mtoken, setMtoken] = useState<string>('');
  const [mobileNumber, setMobileNumber] = useState<string>('');
  const [smsMsg, setSmsMsg] = useState<string>('');
  const [smfaToken, setSmfaToken] = useState<string>('');
  const [isResendRequest, setIsResendRequest] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile(SIZES.md);

  const {
    error: verifyUserError,
    fetchingState: verifyingUserIdentity,
    getData: verifyUserIdentityFn,
    data: verificationResponse,
  } = useNoodleApi(tsClient.creditReportsB2C.verifyUserIdentity);

  const {
    fetchingState: sendingVerificationLink,
    getData: sendVerificationLinkFn,
    data: verificationLinkResponse,
  } = useNoodleApi(tsClient.creditReportsB2C.sendVerificationLink, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'There was an error sending the verification link. Please try again.',
    ],
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Verification link sent successfully!'],
  });

  const {
    getData: verifySmfaStatusFn,
  } = useNoodleApi(tsClient.creditReportsB2C.verifySmfaStatus, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'There was an error verifying SMFA status. Please try again.',
    ],
    toastOnError: true,
  });

  const { fetchingState: pullingCreditReportData, getData: pullCreditReportDataFn } = useNoodleApi(tsClient.creditReportsB2C.pullCreditReportData, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'There was an error pulling credit report data. Please try again.',
    ],
    toastOnError: true,
  });

  const nextStep = (): void => {
    if (currentStep === steps.length - 1) {
      onConfirm();
    } else {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const handleUserVerification = async ({ email, userInfo }: { email: string; userInfo: ApiModels.CreditReportUserInfo }): Promise<void> => {
    verifyUserIdentityFn({
      creditReportId,
      email,
      userInfo,
    });
  };

  const handleSendVerificationLink = async (): Promise<void> => {
    sendVerificationLinkFn({
      creditReportId,
      mtoken,
    });
  };

  const handleSmfaVerification = async (): Promise<void> => {
    setIsSubmitting(true);
    const verifyStatusResponse = await verifySmfaStatusFn({
      creditReportId,
      smfaToken,
    });

    if (verifyStatusResponse.data?.idpass) {
      await pullCreditReportDataFn({
        creditReportId,
      });
      nextStep();
    } else {
      setCurrentStep(2);
    }

    setIsSubmitting(false);
  };

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  useEffect(() => {
    if (verificationResponse) {
      const { expires, mobile, token } = verificationResponse;

      if (expires && mobile && token) {
        setMtoken(token);
        setMobileNumber(mobile);

        nextStep();
      } else {
        setCurrentStep(3);
      }
    }
  }, [verificationResponse]);

  useEffect(() => {
    if (verificationLinkResponse) {
      const { expires, smsMessage, token } = verificationLinkResponse;

      if (expires && smsMessage && token) {
        setSmsMsg(smsMessage);
        setSmfaToken(token);
      }

      if (!isResendRequest) {
        nextStep();
      }

      setIsResendRequest(false);
    }
  }, [verificationLinkResponse]);

  return (
    <Modal
      // hasUnsavedChanges
      isFullScreen={isMobile}
      title={steps[currentStep].title}
      onClose={pullingCreditReportData.isFetching || currentStep === steps.length - 1 ? undefined : onClose}
      ref={modalRef}
      closeIcon={currentStep !== 0 ? <ArrowCircleLeft weight="fill" size={24} color="var(--color-gray-100)" /> : <XCircle weight="fill" size={24} color="var(--color-gray-100)" />}
    >
      <div className={s.content}>
        {verifyUserError && (
          <>
            <BannerStatus status='error' label={`Failed to verify identity: ${verifyUserError.errors?.[0]}\nError code: ${(verifyUserError.data as { errorCode: string })?.errorCode}\nCorrelation ID: ${verifyUserError.correlationId}\nPlease reach out to support for more information.`} />
            <Spacer size={16} />
          </>
        )}
        {currentStep === 0 && (
          <>
            {verifyingUserIdentity.isFetching && <ProgressIndicator isCentered />}

            {!verifyingUserIdentity.isFetching && <CreditReportForm handleUserVerification={handleUserVerification} />}
          </>
        )}

        {currentStep === 1 && (
          <>
            {sendingVerificationLink.isFetching && <ProgressIndicator isCentered />}

            {!sendingVerificationLink.isFetching && (
              <RequestIdentityVerification mobile={mobileNumber} handleSendVerificationLink={handleSendVerificationLink} />
            )}
          </>
        )}

        {currentStep === 2 && (
          <>
            {isSubmitting && <ProgressIndicator isCentered />}

            {!isSubmitting && (
              <ConfirmIdentityVerification
                mobile={mobileNumber}
                smsMsg={smsMsg}
                handleSmfaVerification={handleSmfaVerification}
                handleSendVerificationLink={handleSendVerificationLink}
                setIsResendRequest={setIsResendRequest}
              />
            )}
          </>
        )}

        {currentStep === 3 && <Confirm nextStep={nextStep} />}
      </div>
    </Modal>
  );
};

export default CustomCreditReportApprovalModal;
